import { Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	SetupWizardLessonFormPayload,
	LESSON_CSV_LABELS,
	LessonCsvKeys,
	LessonCsvTemplate,
	LESSON_CSV_SPEC,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextField } from '../../../fields/setup-wizard/text-field/text-field';
import { CheckboxField } from '../../../fields/setup-wizard/checkbox-field/checkbox-field';
import { IntegerField } from '../../../fields/setup-wizard/integer-field/integer-field';
import { LanguageDropdownField } from '../../../fields/setup-wizard/language-dropdown-field/language-dropdown-field';
import { TextareaField } from '../../../fields/setup-wizard/textarea-field/textarea-field';
import { FormUtil } from '../../../../utils/form.util';

type LessonForm = {
	[LessonCsvKeys.ssoLessonId]: FormControl<string | null>;
	[LessonCsvKeys.ssoUnitId]: FormControl<string | null>;
	[LessonCsvKeys.defaultLangId]: FormControl<string | null>;
	[LessonCsvKeys.displayOrder]: FormControl<number | null>;
	[LessonCsvKeys.enName]: FormControl<string | null>;
	[LessonCsvKeys.enImageUri]: FormControl<string | null>;
	[LessonCsvKeys.enDescriptionShort]: FormControl<string | null>;
	[LessonCsvKeys.enDescriptionHtml]: FormControl<string | null>;
	[LessonCsvKeys.esName]: FormControl<string | null>;
	[LessonCsvKeys.esImageUri]: FormControl<string | null>;
	[LessonCsvKeys.esDescriptionShort]: FormControl<string | null>;
	[LessonCsvKeys.esDescriptionHtml]: FormControl<string | null>;
	[LessonCsvKeys.lessonUuid]: FormControl<string | null>;
	[LessonCsvKeys.unitUuid]: FormControl<string | null>;
	[LessonCsvKeys.active]: FormControl<boolean | null>;
};

@Component({
	selector: 'app-setup-wizard-lesson-form',
	imports: [
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextField,
		CheckboxField,
		IntegerField,
		LanguageDropdownField,
		TextareaField,
	],
	standalone: true,
	templateUrl: './setup-wizard-lesson-form.html',
	styleUrl: './setup-wizard-lesson-form.scss',
})
export class SetupWizardLessonForm {
	public unitUuid = input.required<string>();
	public edit = input<LessonCsvTemplate | null>(null);
	public loading = input<boolean>(false);
	public onClear = output<void>();
	public onSubmit = output<SetupWizardLessonFormPayload>();
	public LessonCsvKeys = LessonCsvKeys;
	public LESSON_CSV_LABELS = LESSON_CSV_LABELS;
	public Validators = Validators;

	public form = new FormGroup<LessonForm>({
		[LessonCsvKeys.ssoLessonId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.ssoUnitId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.defaultLangId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.displayOrder]: new FormControl<number | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.enName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.enImageUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.enDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.enDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.esName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.esImageUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.esDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.esDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.lessonUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.unitUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LessonCsvKeys.active]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
	});

	constructor() {
		FormUtil.applyCsvSpecValidators(this.form, LESSON_CSV_SPEC);
	}

	ngOnChanges() {
		const edit = this.edit();

		if (edit) {
			// If edit contains a lessonUuid we can remove all validators for ssoLessonId
			if (edit[LessonCsvKeys.lessonUuid]) {
				this.form.get(LessonCsvKeys.ssoLessonId)?.clearValidators();
				this.form.get(LessonCsvKeys.ssoLessonId)?.updateValueAndValidity();
			}

			// If edit contains a unitUuid we can remove all validators for ssoUnitId
			if (edit[LessonCsvKeys.unitUuid]) {
				this.form.get(LessonCsvKeys.ssoUnitId)?.clearValidators();
				this.form.get(LessonCsvKeys.ssoUnitId)?.updateValueAndValidity();
			}

			this.form.patchValue({
				[LessonCsvKeys.ssoLessonId]: edit[LessonCsvKeys.ssoLessonId],
				[LessonCsvKeys.ssoUnitId]: edit[LessonCsvKeys.ssoUnitId],
				[LessonCsvKeys.defaultLangId]: edit[LessonCsvKeys.defaultLangId],
				[LessonCsvKeys.displayOrder]: edit[LessonCsvKeys.displayOrder],
				[LessonCsvKeys.enName]: edit[LessonCsvKeys.enName],
				[LessonCsvKeys.enImageUri]: edit[LessonCsvKeys.enImageUri],
				[LessonCsvKeys.enDescriptionShort]: edit[LessonCsvKeys.enDescriptionShort],
				[LessonCsvKeys.enDescriptionHtml]: edit[LessonCsvKeys.enDescriptionHtml],
				[LessonCsvKeys.esName]: edit[LessonCsvKeys.esName],
				[LessonCsvKeys.esImageUri]: edit[LessonCsvKeys.esImageUri],
				[LessonCsvKeys.esDescriptionShort]: edit[LessonCsvKeys.esDescriptionShort],
				[LessonCsvKeys.esDescriptionHtml]: edit[LessonCsvKeys.esDescriptionHtml],
				[LessonCsvKeys.lessonUuid]: edit[LessonCsvKeys.lessonUuid],
				[LessonCsvKeys.unitUuid]: edit[LessonCsvKeys.unitUuid],
				[LessonCsvKeys.active]: edit[LessonCsvKeys.active],
			});
		} else {
			const unitUuid = this.unitUuid();

			if (!unitUuid) {
				throw new Error('Unit UUID is required');
			}

			this.form.patchValue({
				[LessonCsvKeys.unitUuid]: this.unitUuid(),
			});
		}
	}

	onFormSubmit() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			this.onSubmit.emit({
				[LessonCsvKeys.ssoLessonId]: value[LessonCsvKeys.ssoLessonId] ?? undefined,
				[LessonCsvKeys.ssoUnitId]: value[LessonCsvKeys.ssoUnitId] ?? undefined,
				[LessonCsvKeys.defaultLangId]: value[LessonCsvKeys.defaultLangId] ?? undefined,
				[LessonCsvKeys.displayOrder]: value[LessonCsvKeys.displayOrder] ?? undefined,
				[LessonCsvKeys.enName]: value[LessonCsvKeys.enName] ?? undefined,
				[LessonCsvKeys.enImageUri]: value[LessonCsvKeys.enImageUri] ?? undefined,
				[LessonCsvKeys.enDescriptionShort]: value[LessonCsvKeys.enDescriptionShort] ?? undefined,
				[LessonCsvKeys.enDescriptionHtml]: value[LessonCsvKeys.enDescriptionHtml] ?? undefined,
				[LessonCsvKeys.esName]: value[LessonCsvKeys.esName] ?? undefined,
				[LessonCsvKeys.esImageUri]: value[LessonCsvKeys.esImageUri] ?? undefined,
				[LessonCsvKeys.esDescriptionShort]: value[LessonCsvKeys.esDescriptionShort] ?? undefined,
				[LessonCsvKeys.esDescriptionHtml]: value[LessonCsvKeys.esDescriptionHtml] ?? undefined,
				[LessonCsvKeys.lessonUuid]: value[LessonCsvKeys.lessonUuid] ?? undefined,
				[LessonCsvKeys.unitUuid]: value[LessonCsvKeys.unitUuid] ?? undefined,
				[LessonCsvKeys.active]: value[LessonCsvKeys.active] ?? undefined,
			});

			this.form.reset();
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.onClear.emit();
	}
}
